import { Link, NavLink } from "react-router-dom";

const NAV = [
  { label: "Overview", to: "/" },
  { label: "Quickstart — TypeScript", to: "/quickstart/typescript" },
  { label: "Quickstart — Python", to: "/quickstart/python" },
  { label: "API playground", to: "/playground" },
  { label: "API schema (YAML)", to: "/schemas/hub-openapi.yaml", external: true },
  { label: "Partner endpoint contract", to: "/schemas/partner-endpoint.schema.json", external: true },
];

const AI_NAV = [
  { label: "llms.txt", to: "/llms.txt" },
  { label: "AGENTS.md", to: "/AGENTS.md" },
  { label: "system-prompt.txt", to: "/system-prompt.txt" },
];

export function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100">
      <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white/85 backdrop-blur dark:border-neutral-800 dark:bg-neutral-950/85">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-6 py-3.5">
          <Link to="/" className="text-sm font-semibold tracking-tight">
            AppSumo Outcomes <span className="text-neutral-400">· docs</span>
          </Link>
          <nav className="hidden items-center gap-5 text-sm md:flex">
            <NavLink
              to="/quickstart/typescript"
              className={({ isActive }) =>
                isActive
                  ? "font-medium text-emerald-600 dark:text-emerald-400"
                  : "text-neutral-600 hover:text-emerald-600 dark:text-neutral-400 dark:hover:text-emerald-400"
              }
            >
              Quickstart
            </NavLink>
            <NavLink
              to="/playground"
              className={({ isActive }) =>
                isActive
                  ? "font-medium text-emerald-600 dark:text-emerald-400"
                  : "text-neutral-600 hover:text-emerald-600 dark:text-neutral-400 dark:hover:text-emerald-400"
              }
            >
              Playground
            </NavLink>
            <a
              href="https://hub.sumo.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-neutral-600 hover:text-emerald-600 dark:text-neutral-400 dark:hover:text-emerald-400"
            >
              hub.sumo.com ↗
            </a>
          </nav>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl gap-10 px-6 py-10 lg:flex-row flex-col">
        <aside className="w-full shrink-0 lg:w-60">
          <nav className="sticky top-24 space-y-6 text-sm">
            <NavSection title="Get started" items={NAV} />
            <NavSection title="LLM / AI context" items={AI_NAV} />
          </nav>
        </aside>

        <main className="min-w-0 flex-1 pb-12">{children}</main>
      </div>

      <footer className="border-t border-neutral-200 px-6 py-8 text-sm text-neutral-500 dark:border-neutral-800">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <span>© AppSumo Outcomes</span>
          <span>
            Hub API <code className="font-mono text-xs">2026-04-16</code>
          </span>
        </div>
      </footer>
    </div>
  );
}

function NavSection({
  title,
  items,
}: {
  title: string;
  items: { label: string; to: string; external?: boolean }[];
}) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {title}
      </p>
      <ul className="space-y-1">
        {items.map((item) =>
          item.external ? (
            <li key={item.to}>
              <a
                href={item.to}
                className="block rounded px-2 py-1.5 text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-900"
              >
                {item.label} ↗
              </a>
            </li>
          ) : (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `block rounded px-2 py-1.5 ${
                    isActive
                      ? "bg-emerald-50 font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                      : "text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-900"
                  }`
                }
              >
                {item.label}
              </NavLink>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}
